import { RecordingType } from 'js/src/components/app/recorder/types';
import { BaseRecorder } from 'js/src/libs/recorder/base-recorder';
import {
    getAvailableDevicesForType,
    getDeviceIdFromTrack,
    getFirstAudioTrackFromStream,
    stopAndRemoveAllAudioTracksOtherThanFirstFromStream,
    stopAndRemoveAllTracksFromStream,
    stopAndRemoveAllVideoTracksFromStream,
    stopAndRemoveTrackFromStream,
} from 'js/src/libs/recorder/recorder-utils';

export class VoiceoverRecorder extends BaseRecorder {
    override recordingType: RecordingType = RecordingType.AUDIO;

    private outputAudioContext: AudioContext | null = null;
    private outputAudioDestination: MediaStreamAudioDestinationNode | null = null;
    private outputAudioTrack: MediaStreamTrack | null = null;

    private microphoneInputStream: MediaStream | null = null;
    private microphoneSourceNode: MediaStreamAudioSourceNode | null = null;

    private systemAudioSourceNode: MediaStreamAudioSourceNode | null = null;

    override get isMicrophoneConnected(): boolean {
        return this.microphoneInputStream !== null;
    }

    override get micTrack(): MediaStreamTrack | null {
        return this.microphoneInputStream?.getAudioTracks()[0] ?? null;
    }

    override checkSupport() {
        super.checkSupport();
        if (!('getUserMedia' in navigator.mediaDevices)) {
            throw new Error(
                'Navigator.mediaDevices.getUserMedia is not available in this environment.',
            );
        }
    }

    override async checkPermissions() {
        await super.checkPermissions();
        await this.checkMicrophonePermission();
    }

    async checkMicrophonePermission() {
        let permission = false;
        if (!permission) {
            permission = await this.checkMicrophonePermissionUsingPermissionsAPI();
        }
        if (!permission) {
            permission = await this.checkMicrophonePermissionUsingEnumerateDevices();
        }
        if (!permission) {
            permission = await this.checkMicrophonePermissionUsingGetUserMedia();
        }
        if (!permission) {
            throw new Error('Microphone permission not granted.');
        }
    }

    async checkMicrophonePermissionUsingPermissionsAPI() {
        try {
            const { state } = await navigator.permissions.query({
                name: 'microphone' as PermissionName,
            });
            return state === 'granted';
        } catch (error) {
            return false;
        }
    }

    async checkMicrophonePermissionUsingEnumerateDevices() {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            return devices
                .filter((device) => device.kind === 'audioinput')
                .some((device) => device.deviceId && device.label);
        } catch (error) {
            return false;
        }
    }

    async checkMicrophonePermissionUsingGetUserMedia() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: false,
                audio: true,
            });
            stopAndRemoveAllTracksFromStream(stream);
            return true;
        } catch (error) {
            return false;
        }
    }

    override async initialize() {
        await super.initialize();

        const context = new AudioContext();
        this.outputAudioContext = context;

        const destination = context.createMediaStreamDestination();
        this.outputAudioDestination = destination;

        stopAndRemoveAllVideoTracksFromStream(destination.stream);
        stopAndRemoveAllAudioTracksOtherThanFirstFromStream(destination.stream);
        const audioTrack = getFirstAudioTrackFromStream(destination.stream);
        this.addTrackToMediaRecorderStream(audioTrack);
        this.outputAudioTrack = audioTrack;
    }

    override async initializeMicrophoneDevices() {
        const { devices, defaultDeviceId } = await getAvailableDevicesForType('audioinput');
        this.availableMicrophoneDevices = devices;
        this.defaultMicrophoneDeviceId = defaultDeviceId;
        this.activeMicrophoneDeviceId = defaultDeviceId;
    }

    override async connectMicrophone(deviceId?: string) {
        this.disconnectMicrophone();

        const targetDeviceId = deviceId || this.activeMicrophoneDeviceId;
        const constraints: MediaStreamConstraints = {
            audio: targetDeviceId ? { deviceId: { exact: targetDeviceId } } : true,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        // The recorder can be torn down (e.g. the modal closed) while
        // `getUserMedia` is still resolving. `destroy()` tears down the output
        // AudioContext, so if it's gone, release the just-acquired mic and bail
        // rather than wiring it into a dead context (which would throw and leak
        // a live microphone track). `initialize()` always (re)establishes the
        // context before the mic is connected, so a null here means teardown.
        if (!this.outputAudioContext || !this.outputAudioDestination) {
            stopAndRemoveAllTracksFromStream(stream);
            return;
        }
        stopAndRemoveAllVideoTracksFromStream(stream);
        stopAndRemoveAllAudioTracksOtherThanFirstFromStream(stream);
        this.microphoneInputStream = stream;

        const track = getFirstAudioTrackFromStream(stream);
        this.activeMicrophoneDeviceId = getDeviceIdFromTrack(track);

        const source = this.outputAudioContext.createMediaStreamSource(stream);
        this.microphoneSourceNode = source;
        source.connect(this.outputAudioDestination);
    }

    override disconnectMicrophone() {
        stopAndRemoveAllTracksFromStream(this.microphoneInputStream);
        this.microphoneInputStream = null;
        if (this.microphoneSourceNode) {
            this.microphoneSourceNode.disconnect();
            this.microphoneSourceNode = null;
        }
    }

    /**
     * Route an additional audio track (e.g. captured system/tab audio from
     * `getDisplayMedia`) into the same output destination as the microphone, so
     * the two are mixed into the single recorded audio track. The caller owns
     * the track's lifecycle (stopping it when its source stream is torn down).
     */
    protected connectSystemAudioTrack(track: MediaStreamTrack) {
        if (!this.outputAudioContext || !this.outputAudioDestination) {
            return;
        }
        this.disconnectSystemAudio();
        const source = this.outputAudioContext.createMediaStreamSource(new MediaStream([track]));
        this.systemAudioSourceNode = source;
        source.connect(this.outputAudioDestination);
    }

    protected disconnectSystemAudio() {
        if (this.systemAudioSourceNode) {
            this.systemAudioSourceNode.disconnect();
            this.systemAudioSourceNode = null;
        }
    }

    private disconnectOutputAudioStreamFromMediaRecorderStream() {
        this.destroyOutputAudioTrack();
        this.destroyOutputAudioDestination();
        this.destroyOutputAudioContext();
    }

    private destroyOutputAudioTrack() {
        if (this.outputAudioTrack) {
            stopAndRemoveTrackFromStream(this.mediaRecorderStream, this.outputAudioTrack);
            this.outputAudioTrack.stop();
            this.outputAudioTrack = null;
        }
    }

    private destroyOutputAudioDestination() {
        if (this.outputAudioDestination) {
            stopAndRemoveAllTracksFromStream(this.outputAudioDestination.stream);
            this.outputAudioDestination.disconnect();
            this.outputAudioDestination = null;
        }
    }

    private async destroyOutputAudioContext() {
        if (this.outputAudioContext) {
            try {
                await this.outputAudioContext.close();
            } catch (error) {
                // do nothing.
            } finally {
                this.outputAudioContext = null;
            }
        }
    }

    override destroy() {
        this.disconnectMicrophone();
        this.disconnectSystemAudio();
        this.disconnectOutputAudioStreamFromMediaRecorderStream();
        super.destroy();
    }
}
